import { useNavigationContainerRef, usePathname } from 'expo-router';
import Head from 'expo-router/head';
import React, { useEffect, useMemo, useCallback, Suspense, lazy } from 'react';
import { Toaster } from 'sonner';
import { Provider as JotaiProvider } from 'jotai';
import * as Sentry from '@sentry/react-native';
import { isRunningInExpoGo } from 'expo';
import { View, StyleSheet, Text, Platform } from 'react-native';
import Constants from 'expo-constants';

import packageJson from '../../package.json';

import Colors from '@/constants/Colors';
import { Meta } from '@/Data';
import CustomFooter from '@/components/Footer';
import { AuthProvider } from '@/providers/auth';
import { Header } from '@/components/header';
import { JotaiStore } from '@/states/store';
import { QueryProvider } from '@/client';
import { ScrollToTop } from '@/components/scroll-to-top';
import { useAppFonts } from '@/hooks/use-fonts';
import { Environment } from '@/env';
import { OptimizedSlot } from '@/components/optimized-slot';
import { AmplitudeContextProvider } from '@/providers/analytics';
import { Button } from '@/components/button';

// Lazy-load heavy components that aren't needed immediately
const ModalProvider = lazy(() =>
  import('@/providers/modal').then((mod) => ({ default: mod.ModalProvider }))
);

// Static data moved outside component to prevent recreation
const SITE_CONFIG = {
  title: 'Reactiive',
  author: '@reactiive_',
  description:
    'Discover the beauty of React Native and how to build stunning animations with Reanimated and Skia',
} as const;

const BASE_URL = 'https://reactiive.io';
const SECURE_BASE_URL = 'https://www.reactiive.io';

// Static meta properties that don't change
const STATIC_META_PROPS = [
  { property: 'og:site_name', content: 'Reactiive' },
  { property: 'og:type', content: 'website' },
  { name: 'twitter:card', content: 'summary' },
  { name: 'twitter:creator', content: SITE_CONFIG.author },
  {
    key: 'viewport',
    name: 'viewport',
    content:
      'width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=1.00001,viewport-fit=cover',
  },
] as const;

// Sentry configuration moved outside component
const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: !isRunningInExpoGo(),
});

Sentry.init({
  dsn: Environment.SENTRY_DSN,
  debug: __DEV__,
  tracesSampleRate: __DEV__ ? 1.0 : 0.1,
  profilesSampleRate: __DEV__ ? 1.0 : 0.01, // Enable profiling: 100% in dev, 1% in prod
  release: packageJson.version, // Dynamic version from package.json
  integrations: [navigationIntegration],
  enableNativeFramesTracking: !isRunningInExpoGo(),
  environment: __DEV__ ? 'development' : 'production',

  // Filter out known/expected errors and sensitive data
  beforeSend(event) {
    // Filter out network errors that are expected
    if (event.exception?.values?.[0]?.type === 'NetworkError') {
      return null;
    }

    // Filter out common React Native warnings in production
    if (!__DEV__ && event.exception?.values?.[0]?.value?.includes('Warning:')) {
      return null;
    }

    // Remove sensitive data
    if (event.request?.cookies) {
      delete event.request.cookies;
    }

    // Remove auth tokens from headers
    if (event.request?.headers) {
      const headers = { ...event.request.headers };
      delete headers.authorization;
      delete headers.cookie;
      event.request.headers = headers;
    }

    // Add additional context
    event.tags = {
      ...event.tags,
      platform: Platform.OS,
      expo_version: Constants.expoVersion || 'unknown',
      is_expo_go: isRunningInExpoGo(),
    };

    return event;
  },

  // Filter sensitive breadcrumbs
  beforeBreadcrumb(breadcrumb) {
    // Filter out console logs that might contain sensitive data
    if (breadcrumb.category === 'console' && breadcrumb.data?.arguments) {
      const args = breadcrumb.data.arguments;
      if (
        args.some(
          (arg: any) =>
            typeof arg === 'string' &&
            (arg.includes('password') ||
              arg.includes('token') ||
              arg.includes('secret'))
        )
      ) {
        return null;
      }
    }

    // Filter out navigation to auth-related routes
    if (breadcrumb.category === 'navigation' && breadcrumb.data?.to) {
      if (
        breadcrumb.data.to.includes('/api/auth') ||
        breadcrumb.data.to.includes('/api/login')
      ) {
        breadcrumb.data = { ...breadcrumb.data, to: '[REDACTED_AUTH_ROUTE]' };
      }
    }

    return breadcrumb;
  },
});

// Utility function optimized
export function ensureSlash(inputPath: string, needsSlash: boolean): string {
  const hasSlash = inputPath.startsWith('/');
  if (hasSlash && !needsSlash) {
    return inputPath.slice(1);
  } else if (!hasSlash && needsSlash) {
    return `/${inputPath}`;
  }
  return inputPath;
}

// Error boundary fallback component memoized
const ErrorFallback = ({
  error,
}: {
  error: unknown;
  componentStack: string;
  eventId: string;
}) => {
  const fullReload = useCallback(() => {
    window.location.reload();
  }, []);

  // @ts-ignore
  const errorDescription = error?.message?.includes('/_expo/static/js/web/')
    ? 'A new version of the website has been deployed. Please refresh the page.'
    : 'An unexpected error has occurred.';

  return (
    <View style={styles.errorContainer}>
      <Text style={styles.errorTitle}>Something went wrong 🙈</Text>
      <Text style={styles.errorMessage}>
        {error instanceof Error && __DEV__ ? error.message : errorDescription}
      </Text>
      <Button variant="outline" onClick={fullReload} size="md">
        Try again
      </Button>
    </View>
  );
};

interface MetaTag {
  name?: string;
  property?: string;
  content: string;
  key?: string;
}

function RootLayout() {
  const { fontsLoaded } = useAppFonts();
  const themeColor = Colors.theme;
  const pathname = usePathname();
  const ref = useNavigationContainerRef();

  // Memoize current path calculation
  const currentPath = useMemo(
    () => ensureSlash(pathname || '', false) || 'home',
    [pathname]
  );

  // Memoize meta data calculation
  const { image, title, description } = useMemo(() => {
    const metaData = (Meta[currentPath as keyof typeof Meta] || Meta.home) as {
      image: {
        url: string;
        width: number;
        height: number;
        description: string;
      };
      title: string;
      description: string;
    };

    return {
      image: { ...metaData.image },
      title: metaData.title || SITE_CONFIG.title,
      description: metaData.description || SITE_CONFIG.description,
    };
  }, [currentPath]);

  // Memoize dynamic meta tags
  const dynamicMetaTags = useMemo((): MetaTag[] => {
    const imageUrl = `${BASE_URL}${image.url}`;
    const secureImageUrl = `${BASE_URL}/${image.url.replace(/^\/+/, '')}`;
    const pageUrl = `${SECURE_BASE_URL}/${currentPath}`;

    return [
      { name: 'description', content: description },
      { property: 'og:description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:url', content: pageUrl },
      { property: 'og:image', content: imageUrl },
      { property: 'og:image:secure_url', content: secureImageUrl },
      { property: 'og:image:width', content: String(image.width) },
      { property: 'og:image:height', content: String(image.height) },
      { property: 'og:image:alt', content: image.description },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: imageUrl },
      { name: 'msapplication-TileColor', content: themeColor },
      { name: 'theme-color', content: themeColor },
    ];
  }, [image, title, description, currentPath, themeColor]);

  // Combine static and dynamic meta tags
  const allMetaTags = useMemo(
    () => [...dynamicMetaTags, ...STATIC_META_PROPS] as MetaTag[],
    [dynamicMetaTags]
  );

  // Memoize site title
  const siteTitle = useMemo(
    () =>
      title === SITE_CONFIG.title ? title : `${title} | ${SITE_CONFIG.title}`,
    [title]
  );

  // Memoized error boundary capture callback
  const beforeCapture = useCallback(
    (scope: Sentry.Scope) => {
      scope.setTag('errorBoundary', true);
      scope.setContext('route', { pathname });
    },
    [pathname]
  );

  // Combined useEffect for navigation and error handling setup
  useEffect(() => {
    // Register navigation container
    if (ref?.current) {
      navigationIntegration.registerNavigationContainer(ref);
    }

    // Global error handler for unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);

      Sentry.captureException(event.reason, {
        tags: {
          error_type: 'unhandled_promise_rejection',
          pathname: pathname || 'unknown',
        },
        contexts: {
          rejection: {
            type: event.type,
            promise: event.promise?.toString() || 'unknown',
          },
        },
      });

      event.preventDefault();
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, [ref, pathname]);

  return (
    <>
      <Head>
        <title>{siteTitle}</title>
        {allMetaTags.map((props, index) => (
          <meta {...props} key={props.key || `meta-${index}`} />
        ))}
      </Head>
      <Toaster theme="dark" duration={2000} />

      <Sentry.ErrorBoundary
        fallback={ErrorFallback}
        beforeCapture={beforeCapture}
      >
        <QueryProvider>
          <JotaiProvider store={JotaiStore}>
            <AmplitudeContextProvider>
              <AuthProvider>
                <Suspense fallback={null}>
                  <ModalProvider />
                </Suspense>
                <Header />
                <View style={styles.mainContainer}>
                  <OptimizedSlot />
                </View>
                <ScrollToTop />
                <CustomFooter />
              </AuthProvider>
            </AmplitudeContextProvider>
          </JotaiProvider>
        </QueryProvider>
      </Sentry.ErrorBoundary>
    </>
  );
}

const styles = StyleSheet.create({
  mainContainer: {
    minHeight: '80vh' as any,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#fff',
  },
  errorMessage: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 20,
    color: '#ccc',
  },
  errorButton: {
    fontSize: 16,
    color: '#007AFF',
    textDecorationLine: 'underline',
    cursor: 'pointer' as any,
  },
});

export default Sentry.wrap(React.memo(RootLayout));
